import { useCallback, useMemo, useState } from 'react';

import type { ParsedIdea } from '../hooks/useBulkEpisodeOrchestrator';
import type {
  CheckUniquenessResponse,
  IdeaUniquenessResult,
  WithinBatchDuplicate,
} from '../services/uniquenessApi';

interface BulkIdeasReviewTableProps {
  ideas: ParsedIdea[];
  uniquenessData: CheckUniquenessResponse | null;
  selectedIndices: Set<number>;
  onSelectionChange: (indices: Set<number>) => void;
  similarityThreshold: number;
  onThresholdChange: (threshold: number) => void;
  disabled?: boolean;
}

const SKILL_LEVEL_LABELS: Record<number, string> = {
  1: 'A1',
  2: 'A2',
  3: 'B1',
  4: 'B2',
  5: 'C1',
};

function getSimilarityColor(similarity: number): string {
  if (similarity >= 0.9) return 'text-red-700 bg-red-50';
  if (similarity >= 0.85) return 'text-orange-700 bg-orange-50';
  if (similarity >= 0.8) return 'text-yellow-700 bg-yellow-50';
  return 'text-green-700 bg-green-50';
}

function getUniquenessInfo(
  ideaIndex: number,
  results: IdeaUniquenessResult[],
  withinBatch: WithinBatchDuplicate[],
  threshold: number
): { isUnique: boolean; matchInfo: string | null; maxSimilarity: number | null } {
  const result = results.find((r) => r.ideaIndex === ideaIndex);
  const batchDups = withinBatch.filter((d) => d.ideaA === ideaIndex || d.ideaB === ideaIndex);

  // Compute max similarity across DB match and batch duplicates
  const dbSimilarity = result?.closestMatch?.similarity ?? 0;
  const batchMaxSimilarity =
    batchDups.length > 0 ? Math.max(...batchDups.map((d) => d.similarity)) : 0;
  const maxSimilarity = Math.max(dbSimilarity, batchMaxSimilarity);

  const aboveThresholdBatchDup = batchDups.find((d) => d.similarity >= threshold);

  if (result?.closestMatch && result.closestMatch.similarity >= threshold) {
    return {
      isUnique: false,
      matchInfo: `${result.closestMatch.similarity.toFixed(2)} "${result.closestMatch.episodeName}": ${result.closestMatch.scenarioSnippet}`,
      maxSimilarity,
    };
  }

  if (aboveThresholdBatchDup) {
    const otherIndex =
      aboveThresholdBatchDup.ideaA === ideaIndex
        ? aboveThresholdBatchDup.ideaB
        : aboveThresholdBatchDup.ideaA;
    return {
      isUnique: false,
      matchInfo: `${aboveThresholdBatchDup.similarity.toFixed(2)} similar to idea #${otherIndex + 1} in batch`,
      maxSimilarity,
    };
  }

  return {
    isUnique: true,
    matchInfo: null,
    maxSimilarity: maxSimilarity > 0 ? maxSimilarity : null,
  };
}

export function BulkIdeasReviewTable({
  ideas,
  uniquenessData,
  selectedIndices,
  onSelectionChange,
  similarityThreshold,
  onThresholdChange,
  disabled,
}: BulkIdeasReviewTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const enrichedIdeas = useMemo(() => {
    if (!uniquenessData) {
      return ideas.map((idea) => ({
        ...idea,
        isUnique: true,
        matchInfo: null as string | null,
        maxSimilarity: null as number | null,
      }));
    }

    return ideas.map((idea) => {
      const info = getUniquenessInfo(
        idea.index,
        uniquenessData.results,
        uniquenessData.withinBatchDuplicates,
        similarityThreshold
      );
      return { ...idea, ...info };
    });
  }, [ideas, uniquenessData, similarityThreshold]);

  const uniqueCount = enrichedIdeas.filter((i) => i.isUnique).length;
  const nonUniqueCount = enrichedIdeas.length - uniqueCount;

  const toggleRow = useCallback((index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const toggleSelection = useCallback(
    (index: number) => {
      if (disabled) return;
      const next = new Set(selectedIndices);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      onSelectionChange(next);
    },
    [selectedIndices, onSelectionChange, disabled]
  );

  const selectAll = useCallback(() => {
    if (disabled) return;
    onSelectionChange(new Set(ideas.map((i) => i.index)));
  }, [ideas, onSelectionChange, disabled]);

  const deselectAll = useCallback(() => {
    if (disabled) return;
    onSelectionChange(new Set());
  }, [onSelectionChange, disabled]);

  const deselectNonUnique = useCallback(() => {
    if (disabled) return;
    const uniqueIndices = new Set(enrichedIdeas.filter((i) => i.isUnique).map((i) => i.index));
    onSelectionChange(uniqueIndices);
  }, [enrichedIdeas, onSelectionChange, disabled]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="mb-4 text-lg font-medium text-gray-900">Step 2: Review Ideas</h3>

      {/* Summary & threshold */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-gray-600">
          {uniquenessData ? (
            <>
              {uniqueCount} unique, {nonUniqueCount} similar to existing &mdash;{' '}
              <strong>{selectedIndices.size} selected</strong> for generation
              {uniquenessData.episodesWithoutEmbeddings > 0 && (
                <span className="ml-2 text-yellow-600">
                  ({uniquenessData.episodesWithoutEmbeddings} episodes without embeddings)
                </span>
              )}
            </>
          ) : (
            <>{ideas.length} ideas loaded &mdash; run uniqueness check to see similarity data</>
          )}
        </div>

        {uniquenessData && (
          <div className="flex items-center gap-2 text-sm">
            <label htmlFor="threshold" className="text-gray-600">
              Threshold:
            </label>
            <input
              id="threshold"
              type="range"
              min={70}
              max={95}
              step={5}
              value={similarityThreshold * 100}
              onChange={(e) => onThresholdChange(parseInt(e.target.value, 10) / 100)}
              disabled={disabled}
              className="w-24"
            />
            <span className="font-mono text-gray-700">{similarityThreshold.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      <div className="mb-3 flex gap-2">
        <button
          type="button"
          onClick={selectAll}
          disabled={disabled}
          className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Select All
        </button>
        <button
          type="button"
          onClick={deselectAll}
          disabled={disabled || selectedIndices.size === 0}
          className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Deselect All
        </button>
        {uniquenessData && nonUniqueCount > 0 && (
          <button
            type="button"
            onClick={deselectNonUnique}
            disabled={disabled}
            className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Deselect Non-Unique
          </button>
        )}
      </div>

      {/* Table */}
      <div className="max-h-[500px] overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 w-10"></th>
              <th className="px-3 py-2 w-10">#</th>
              <th className="px-3 py-2">Idea</th>
              <th className="px-3 py-2 w-16">Level</th>
              <th className="px-3 py-2 w-20">Duration</th>
              {uniquenessData && <th className="px-3 py-2 w-48">Similarity</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {enrichedIdeas.map((idea) => (
              <tr
                key={idea.index}
                className={selectedIndices.has(idea.index) ? 'bg-white' : 'bg-gray-50 opacity-60'}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedIndices.has(idea.index)}
                    onChange={() => toggleSelection(idea.index)}
                    disabled={disabled}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                  />
                </td>
                <td className="px-3 py-2 text-gray-400">{idea.index + 1}</td>
                <td className="px-3 py-2">
                  <div
                    className="max-w-md cursor-pointer truncate"
                    title={idea.idea}
                    onClick={() => toggleRow(idea.index)}
                  >
                    {expandedRows.has(idea.index) ? idea.idea : idea.idea.slice(0, 80)}
                    {!expandedRows.has(idea.index) && idea.idea.length > 80 && '...'}
                  </div>
                  {idea.title && (
                    <div className="mt-0.5 text-xs text-gray-400">Title: {idea.title}</div>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {SKILL_LEVEL_LABELS[idea.skillLevel] || idea.skillLevel}
                </td>
                <td className="px-3 py-2 text-xs">{idea.duration || '-'}</td>
                {uniquenessData && (
                  <td className="px-3 py-2">
                    {idea.isUnique ? (
                      <span className="inline-block rounded-full bg-green-50 px-2 py-0.5 text-xs font-mono text-green-700">
                        {idea.maxSimilarity != null ? idea.maxSimilarity.toFixed(2) : '-'}
                      </span>
                    ) : (
                      <div
                        className={`rounded px-2 py-0.5 text-xs ${getSimilarityColor(idea.maxSimilarity!)}`}
                        title={idea.matchInfo || ''}
                      >
                        {idea.matchInfo && idea.matchInfo.length > 60
                          ? idea.matchInfo.slice(0, 60) + '...'
                          : idea.matchInfo}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
